import { Cpu, Menu, User, LogOut, Bell, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export const Header = () => {
  const { user, profile, signOut } = useAuth();
  const { toast } = useToast();

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: 'Signed out',
      description: 'You have been signed out successfully.',
    });
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/gpu-comparison" className="flex items-center gap-2 font-bold text-lg" aria-label="GPU Compare - Home">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-nvidia to-amd flex items-center justify-center">
            <Cpu className="w-5 h-5 text-white" aria-hidden="true" />
          </div>
          <span className="hidden sm:inline">GPU Compare</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <Link to="/gpu-comparison#quiz" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            GPU Finder
          </Link>
          <Link to="/gpu-comparison#comparison" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Comparison
          </Link>
          <Link to="/gpu-comparison#benchmarks" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Benchmarks
          </Link>
          <Link to="/gpu-comparison#prices" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Prices
          </Link>
          <Link to="/gpu-comparison#compatibility" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Build Checker
          </Link>
          <Link to="/gpu-comparison#faq" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            FAQ
          </Link>
        </nav>

        {/* Auth Section */}
        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{profile?.display_name || user.email?.split('@')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{profile?.display_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/gpu-comparison#prices" className="cursor-pointer">
                    <Bell className="w-4 h-4 mr-2" />
                    My Price Alerts
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/account" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm">
              <Link to="/auth">Sign In</Link>
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <nav className="flex flex-col gap-4 mt-8">
                <Link to="/gpu-comparison#quiz" className="text-lg font-medium hover:text-primary transition-colors">
                  GPU Finder
                </Link>
                <Link to="/gpu-comparison#comparison" className="text-lg font-medium hover:text-primary transition-colors">
                  Comparison
                </Link>
                <Link to="/gpu-comparison#benchmarks" className="text-lg font-medium hover:text-primary transition-colors">
                  Benchmarks
                </Link>
                <Link to="/gpu-comparison#prices" className="text-lg font-medium hover:text-primary transition-colors">
                  Prices
                </Link>
                <Link to="/gpu-comparison#compatibility" className="text-lg font-medium hover:text-primary transition-colors">
                  Build Checker
                </Link>
                <Link to="/gpu-comparison#faq" className="text-lg font-medium hover:text-primary transition-colors">
                  FAQ
                </Link>
                {!user && (
                  <Button asChild className="mt-4">
                    <Link to="/auth">Sign In / Sign Up</Link>
                  </Button>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
